import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import {
  BotanicalCorner,
  GoldLineDivider,
  FlowingWaves,
} from '@/components/feature/DrawingLines';

const storyChapters = [
  {
    number: '01',
    tag: 'The Genesis',
    title: '시작',
    subtitle: '모든 예술은 하나의 \'선\'에서 시작됩니다.',
    paragraphs: [
      '하지만 타투에서의 선은 다른 어떤 예술보다 무겁습니다. 한 번 새겨지면 평생을 고객의 호흡과 함께하며, 그 사람의 시간과 체온 속에 녹아들기 때문입니다.',
      '우리는 질문했습니다. "왜 타투는 어둡고 폐쇄적인 공간에서 이루어져야 하는가?" 그 질문에 대한 답이 바로 선율입니다. 우리는 타투를 음지에서 양지로, 시술에서 예술로, 그리고 찰나의 장식에서 평생의 반려가 되는 \'선율\'로 격상시키고자 합니다.',
    ],
    image: 'https://public.readdy.ai/ai/img_res/33e9c8ed0a8e61d42d86ffb8e1071f5b.jpg',
    imageAlt: '선율의 시작',
  },
  {
    number: '02',
    tag: 'Philosophy',
    title: '철학',
    subtitle: '고결한 선의 미학',
    paragraphs: [
      '선율은 단순히 그림을 그리는 곳이 아닙니다. 고객이 살아온 삶의 이야기, 간직하고 싶은 기억, 혹은 스스로에게 다짐하는 약속들을 정교한 \'선(Line)\'과 절제된 \'율동(Melody)\'으로 변환합니다.',
    ],
    image: 'https://readdy.ai/api/search-image?query=close%20up%20of%20tattoo%20artist%20hand%20holding%20needle%20with%20extreme%20precision%20on%20clean%20white%20skin%2C%20fine%20line%20tattoo%20art%20in%20progress%2C%20medical%20grade%20sterile%20environment%2C%20soft%20studio%20lighting%2C%20macro%20photography%2C%20artistic%20and%20clinical%20aesthetic%2C%20minimal%20background&width=800&height=600&seq=story_philosophy_v1&orientation=landscape',
    imageAlt: '선율의 철학',
    values: [
      {
        icon: 'ri-contrast-2-line',
        keyword: 'Purity',
        label: '순수',
        desc: '갤러리보다 더 정갈한 백색의 공간은 고객의 내면을 오롯이 마주하기 위한 비움의 미학입니다.',
      },
      {
        icon: 'ri-focus-3-line',
        keyword: 'Precision',
        label: '정교',
        desc: '메디컬 수준의 엄격한 위생은 예술을 향한 최소한의 예의이자, 고객에 대한 최대의 약속입니다.',
      },
      {
        icon: 'ri-music-2-line',
        keyword: 'Harmony',
        label: '조화',
        desc: '피부의 결과 근육의 움직임, 그리고 당신의 분위기까지 고려한 최적의 리듬을 찾아냅니다.',
      },
    ],
  },
  {
    number: '03',
    tag: 'Mission',
    title: '약속',
    subtitle: 'Where Lines Become Art',
    paragraphs: [
      '우리는 당신의 몸에 문신을 새기는 것이 아니라, 당신의 인생에 가장 아름다운 \'선율\'을 더해드린다고 믿습니다.',
      '바늘 끝에서 시작된 차가운 선이 당신의 체온을 만나 비로소 따뜻한 예술로 피어나는 곳. 어둡고 거친 타투의 편견을 넘어, 가장 밝고 고귀한 공간에서 당신만의 유일한 걸작을 만나보십시오.',
    ],
    image: 'https://readdy.ai/api/search-image?query=finished%20fine%20line%20tattoo%20artwork%20on%20wrist%20showing%20delicate%20botanical%20illustration%2C%20elegant%20and%20refined%2C%20warm%20skin%20tone%2C%20soft%20natural%20light%2C%20close%20up%20beauty%20photography%2C%20white%20background%2C%20premium%20luxury%20aesthetic%2C%20artistic%20masterpiece&width=800&height=600&seq=story_mission_v1&orientation=landscape',
    imageAlt: '선율의 약속',
  },
];

function ChapterHeader({ number, tag, title, visible }: { number: string; tag: string; title: string; visible: boolean }) {
  return (
    <div
      className="flex items-center gap-3 sm:gap-5 mb-8"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.9s ease-out',
      }}
    >
      <span
        className="text-5xl sm:text-[80px] leading-none text-[#F0EDE8] select-none flex-shrink-0"
        style={{ fontFamily: "'Cinzel', serif", fontWeight: 300 }}
      >
        {number}
      </span>
      <div>
        <p className="text-[9px] sm:text-[10px] tracking-[0.35em] uppercase text-[#C9A96E] mb-1">{tag}</p>
        <h3
          className="text-xl sm:text-2xl md:text-3xl text-[#1A1A1A]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
        >
          {title}
        </h3>
      </div>
    </div>
  );
}

function ChapterOne({ chapter }: { chapter: typeof storyChapters[0] }) {
  const { ref, visible } = useScrollAnimation(0.1);
  return (
    <div ref={ref} className="py-16 sm:py-20 md:py-28 border-t border-[#F0EDE8]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-24 items-center">
          {/* Image — top on mobile */}
          <div
            className="w-full lg:w-1/2 order-1 lg:order-2"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(30px)',
              transition: 'all 1s ease-out 0.2s',
            }}
          >
            <img
              src={chapter.image}
              alt={chapter.imageAlt}
              className="w-full h-[240px] sm:h-[320px] md:h-[420px] lg:h-[460px] object-cover object-top"
            />
          </div>
          {/* Text */}
          <div
            className="w-full lg:w-1/2 order-2 lg:order-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 1s ease-out',
            }}
          >
            <ChapterHeader number={chapter.number} tag={chapter.tag} title={chapter.title} visible={visible} />
            <p
              className="text-base sm:text-lg text-[#1A1A1A] leading-relaxed mb-6 font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {chapter.subtitle}
            </p>
            <div className="space-y-4">
              {chapter.paragraphs.map((p, i) => (
                <p key={i} className="text-sm text-[#777] leading-[1.9] font-light">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChapterTwo({ chapter }: { chapter: typeof storyChapters[1] }) {
  const { ref, visible } = useScrollAnimation(0.08);
  return (
    <div ref={ref} className="py-16 sm:py-20 md:py-28 border-t border-[#F0EDE8] bg-[#FAFAF8]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        {/* Header */}
        <ChapterHeader number={chapter.number} tag={chapter.tag} title={`${chapter.title} — ${chapter.subtitle}`} visible={visible} />

        <div className="flex flex-col lg:flex-row gap-10 lg:gap-24 items-start">
          {/* Image */}
          <div
            className="w-full lg:w-5/12"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 1s ease-out 0.15s',
            }}
          >
            <img
              src={chapter.image}
              alt={chapter.imageAlt}
              className="w-full h-[240px] sm:h-[300px] md:h-[380px] object-cover object-top"
            />
          </div>

          {/* Content */}
          <div className="w-full lg:w-7/12">
            <div
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateX(0)' : 'translateX(30px)',
                transition: 'all 1s ease-out 0.2s',
              }}
            >
              {chapter.paragraphs.map((p, i) => (
                <p key={i} className="text-sm text-[#777] leading-[1.9] font-light mb-5">{p}</p>
              ))}
            </div>

            {/* Three Values */}
            <div className="mt-8 space-y-0">
              {chapter.values?.map((v, i) => (
                <div
                  key={v.keyword}
                  className="flex gap-4 sm:gap-6 py-5 sm:py-7 border-b border-[#EBEBEB] last:border-0 group"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateX(0)' : 'translateX(20px)',
                    transition: 'all 0.8s ease-out',
                    transitionDelay: `${i * 120 + 300}ms`,
                  }}
                >
                  <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center border border-[#E0DDD8] group-hover:border-[#C9A96E] group-hover:bg-[#C9A96E] transition-all duration-300">
                    <i className={`${v.icon} text-sm sm:text-base text-[#AAAAAA] group-hover:text-white transition-colors duration-300`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span
                        className="text-xs sm:text-sm text-[#C9A96E] tracking-[0.12em]"
                        style={{ fontFamily: "'Cinzel', serif", fontWeight: 400 }}
                      >
                        {v.keyword}
                      </span>
                      <span className="text-xs text-[#BBBBBB]">·</span>
                      <span className="text-xs text-[#999] tracking-wider">{v.label}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-[#777] leading-relaxed font-light">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChapterThree({ chapter }: { chapter: typeof storyChapters[2] }) {
  const { ref, visible } = useScrollAnimation(0.1);
  return (
    <div ref={ref} className="py-16 sm:py-20 md:py-28 border-t border-[#F0EDE8]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div className="flex flex-col lg:flex-row-reverse gap-10 lg:gap-24 items-center">
          {/* Image — top on mobile */}
          <div
            className="w-full lg:w-1/2"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 1s ease-out 0.2s',
            }}
          >
            <img
              src={chapter.image}
              alt={chapter.imageAlt}
              className="w-full h-[240px] sm:h-[320px] md:h-[420px] lg:h-[460px] object-cover object-top"
            />
          </div>
          {/* Text */}
          <div
            className="w-full lg:w-1/2"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(30px)',
              transition: 'all 1s ease-out',
            }}
          >
            <ChapterHeader number={chapter.number} tag={chapter.tag} title={chapter.title} visible={visible} />
            <p
              className="text-base sm:text-lg text-[#C9A96E] leading-relaxed mb-6 font-light tracking-wider"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              {chapter.subtitle}
            </p>
            <div className="space-y-4">
              {chapter.paragraphs.map((p, i) => (
                <p key={i} className="text-sm text-[#777] leading-[1.9] font-light">{p}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MainCopyBanner() {
  return (
    <section className="py-20 sm:py-24 md:py-32 bg-[#1A1A1A] relative overflow-hidden">
      <FlowingWaves color="rgba(201,169,110,0.06)" lineCount={5} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.12)" size={180} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.10)" size={150} />
      <div className="relative z-10 max-w-[900px] mx-auto px-5 sm:px-8 md:px-16 text-center">
        <p className="text-[9px] sm:text-[10px] tracking-[0.4em] sm:tracking-[0.5em] uppercase text-[#C9A96E] mb-8 sm:mb-10">Brand Story</p>
        <blockquote
          className="text-xl sm:text-2xl md:text-4xl lg:text-5xl text-white leading-[1.8] font-light mb-8 sm:mb-10"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          <span className="block text-white">&ldquo;당신의 삶이 하나의 음악이라면,</span>
          <span className="block text-white/70">우리는 그 위로 흐르는</span>
          <span className="block text-white">고결한 선율이 되겠습니다.&rdquo;</span>
        </blockquote>
        <div className="w-12 sm:w-16 h-px bg-[#C9A96E]/40 mx-auto mb-6 sm:mb-8" />
        <p
          className="text-xs sm:text-sm text-white/30 leading-relaxed font-light"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
        >
          가장 깨끗한 곳에서, 당신의 가장 깊은 이야기를 꺼내어 보세요.<br />
          선율 라인 스튜디오.
        </p>
      </div>
    </section>
  );
}

export default function BrandStorySection() {
  const { ref, visible } = useScrollAnimation(0.15);

  return (
    <section className="bg-white">
      {/* Section Header */}
      <div ref={ref} className="py-14 sm:py-20 md:py-28 max-w-[1440px] mx-auto px-5 sm:px-8 md:px-16">
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.9s ease-out',
          }}
        >
          <p className="text-[9px] sm:text-[10px] tracking-[0.4em] sm:tracking-[0.5em] uppercase text-[#C9A96E] mb-4 sm:mb-5">Brand Story</p>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#1A1A1A] leading-tight mb-6 sm:mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300 }}
          >
            선율(線律),<br />
            <span className="text-[#AAAAAA]">삶의 궤적을 예술로 잇다</span>
          </h2>
        </div>
        <div
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 1s ease 0.3s',
          }}
        >
          <GoldLineDivider delay={0.3} />
        </div>
      </div>

      {/* Chapter 01 */}
      <ChapterOne chapter={storyChapters[0]} />

      {/* Chapter 02 */}
      <ChapterTwo chapter={storyChapters[1]} />

      {/* Chapter 03 */}
      <ChapterThree chapter={storyChapters[2]} />

      {/* Main Copy Banner */}
      <MainCopyBanner />
    </section>
  );
}
